import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AmountInput } from '../../../core/shared/directives/amount-input.directive';
import { AmountPipe } from '../../../core/shared/directives/amount.pipe';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import {
  FinancialProfile,
  FinancialProfileService,
} from '../shared/services/financial-profile.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { DynamicFormComponent } from '../../../core/shared/dynamic-form/dynamic-form.component';
import { DynamicFieldConfig } from '../../../core/shared/dynamic-form/form-config.model';
import { ExcelUpload } from '../../../core/shared/components/excel-upload/excel-upload';
import {
  ExcelImportService,
  ExcelParseResult,
} from '../../../core/shared/services/excel-import.service';
import { OnboradingService } from '../../onboarding/shared/service/onboarding.service';
import { Setup } from '../../setup/shared/service/setup';
import { CraditService } from '../shared/services/cradit.service';

@Component({
  selector: 'app-income-statement-type-credit-proposal',
  standalone: true,
  imports: [
    AmountInput,
    AmountPipe,
    CommonModule,
    FormsModule,
    DynamicFormComponent,
    ExcelUpload,
    TranslatePipe,
  ],
  templateUrl: './income-statement-type-credit-proposal.html',
  styleUrl: './income-statement-type-credit-proposal.css',
})
export class IncomeStatementTypeCreditProposal
  implements OnInit, OnChanges, OnDestroy {

  @Input() config: any;
  @Input() editable = false;
  @Input() crdtPrpslSeq: number | null = null;
  @Input() onboardingId: number | null = null;
  @Input() sectionId = 'income-statement-type-credit-proposal';

  /**
   * Backend discriminator:
   * typ = 2 means Income Statement.
   */
  readonly INCOME_STATEMENT_TYPE = 2;

  institutionName = '';

  demographicFields: DynamicFieldConfig[] = [
    {
      key: 'financialStatementType',
      label: 'INCOME_STATEMENT.FINANCIAL_STATEMENT_TYPE',
      type: 'select',
      options$: [
        {
          label: 'INCOME_STATEMENT.AUDITED',
          value: 0,
        },
        {
          label: 'INCOME_STATEMENT.MANAGEMENT_UNAUDITED',
          value: 1,
        },
      ],
      validations: {
        required: true,
      },
    },
    {
      key: 'reportingCurrency',
      label: 'INCOME_STATEMENT.REPORTING_CURRENCY',
      type: 'select',
      options$: [
        {
          label: 'PKR',
          value: 0,
        },
        {
          label: 'USD',
          value: 1,
        },
      ],
      validations: {
        required: true,
      },
    },
    {
      key: 'financialYearEnd',
      label: 'INCOME_STATEMENT.FINANCIAL_YEAR_END',
      type: 'date',
      validations: {
        required: true,
      },
    },
    {
      key: 'isConsolidated',
      label: 'INCOME_STATEMENT.CONSOLIDATED_FINANCIALS',
      type: 'radio',
      options$: [
        {
          label: 'COMMON.YES',
          value: true,
        },
        {
          label: 'COMMON.NO',
          value: false,
        },
      ],
    },
  ];

  /** The form as it is while the Financial section has not been saved. */
  private readonly ownFields = this.demographicFields;

  /**
   * Years, Audited / Unaudited per year, reporting currency, year end and
   * consolidation from the Financial section — null until it is saved, in
   * which case this section asks for them itself as before.
   */
  financialProfile: FinancialProfile | null = null;

  financialProfileLoaded = false;

  /** Years this section saved itself (can differ from the Financial section's). */
  private ownSavedYears: string[] = [];

  private financialProfileSub?: Subscription;
  private financialProfileChangedSub?: Subscription;

  demographicModel: any = {};

  years: string[] = [];
  availableYears: string[] = [];
  selectedYear = '';

  /**
   * Years removed via the ✕ icon that still have previously saved data —
   * carried through to `buildFinancialDataList()` so their rows go out
   * with delFlg 1 instead of silently dropping out of the payload.
   */
  private removedYears: string[] = [];

  financialSections: any[] = [];

  saving = false;
  loadingSavedData = false;

  errorMessage = '';
  successMessage = '';

  /** Message shown after an Excel import. */
  fileUploadError = '';

  private saveSub?: Subscription;
  private proposalSequenceSub?: Subscription;

  private financialMasterLoaded = false;

  constructor(
    private setupService: Setup,
    private creditService: CraditService,
    private route: ActivatedRoute,
    private onboardingService: OnboradingService,
    private cdr: ChangeDetectorRef,
    private excelImport: ExcelImportService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private financialProfileService: FinancialProfileService,
  ) {}

  ngOnInit(): void {
    const routeSequence =
      this.getRouteSequence();

    if (
      this.onboardingId === null ||
      Number(this.onboardingId) <= 0
    ) {
      this.onboardingId =
        routeSequence;
    }

    if (
      this.crdtPrpslSeq === null ||
      Number(this.crdtPrpslSeq) <= 0
    ) {
      this.crdtPrpslSeq =
        this.creditService.getCrdtPrpslSeq();
    }

    this.generateAvailableYears();
    this.setDefaultYears();

    this.loadInstitutionName();
    this.loadIncomeStatementMasterData();

    this.proposalSequenceSub =
      this.creditService.crdtPrpslSeq$
        .subscribe((sequence) => {
          const normalizedSequence =
            Number(sequence ?? 0);

          if (
            normalizedSequence <= 0 ||
            normalizedSequence ===
              Number(this.crdtPrpslSeq)
          ) {
            return;
          }

          this.crdtPrpslSeq =
            normalizedSequence;

          if (this.financialMasterLoaded) {
            this.loadSavedFinancialData();
          }
        });

    this.financialProfileChangedSub =
      this.financialProfileService.changed$
        .subscribe(() => this.loadFinancialProfile());

    this.saveSub =
      this.creditService.saveSectionTrigger$
        .subscribe((sectionId) => {
          if (
            this.normalizeSectionId(sectionId) ===
            this.normalizeSectionId(this.sectionId)
          ) {
            this.saveFinancialData();
          }
        });
  }

  private normalizeSectionId(
    value: unknown,
  ): string {
    return String(value ?? '').trim();
  }

  ngOnChanges(
    changes: SimpleChanges,
  ): void {
    if (
      changes['onboardingId'] &&
      this.onboardingId &&
      Number(this.onboardingId) > 0
    ) {
      this.loadInstitutionName();
    }

    if (
      changes['crdtPrpslSeq'] &&
      this.financialMasterLoaded &&
      this.crdtPrpslSeq &&
      Number(this.crdtPrpslSeq) > 0
    ) {
      this.loadSavedFinancialData();
    }
  }

  ngOnDestroy(): void {
    this.saveSub?.unsubscribe();
    this.financialProfileSub?.unsubscribe();
    this.financialProfileChangedSub?.unsubscribe();
    this.proposalSequenceSub?.unsubscribe();
  }

  private loadInstitutionName(): void {
    if (
      this.onboardingId === null ||
      Number(this.onboardingId) <= 0
    ) {
      return;
    }

    this.onboardingService
      .GetOBAprvdList()
      .subscribe({
        next: (response: any) => {
          const possibleLists = [
            response,
            response?.data,
            response?.content,
            response?.data?.content,
            response?.crdtList,
            response?.data?.crdtList,
          ];

          const matchedList =
            possibleLists.find(
              (value: unknown) =>
                Array.isArray(value),
            );

          const list: any[] =
            Array.isArray(matchedList)
              ? matchedList
              : [];

          const currentInstitution =
            list.find(
              (item: any) =>
                Number(
                  item?.crptOnbrdngSeq ??
                  item?.crprtOnbrdngSeq ??
                  item?.onboardingSeq ??
                  0,
                ) ===
                Number(this.onboardingId),
            );

          this.institutionName =
            String(
              currentInstitution?.nmOfInstitution ??
              currentInstitution?.instNm ??
              currentInstitution?.institutionName ??
              '',
            ).trim();

          this.demographicModel = {
            ...this.demographicModel,
            institutionName:
              this.institutionName,
          };

          console.log(
            'Income Statement institution name:',
            this.institutionName,
          );
        },

        error: (error) => {
          console.error(
            'Unable to load institution name:',
            error,
          );

          this.institutionName = '';

          this.demographicModel = {
            ...this.demographicModel,
            institutionName: '',
          };
        },
      });
  }

  getRouteSequence(): number | null {
    let currentRoute:
      ActivatedRoute | null =
      this.route;

    while (currentRoute) {
      const routeId =
        currentRoute.snapshot.paramMap.get('id') ||
        currentRoute.snapshot.paramMap.get('oid');

      const sequence =
        Number(routeId);

      if (
        routeId &&
        Number.isFinite(sequence) &&
        sequence > 0
      ) {
        return sequence;
      }

      currentRoute =
        currentRoute.parent;
    }

    return null;
  }

  generateAvailableYears(): void {
    const currentYear =
      new Date().getFullYear();

    this.availableYears = [];

    for (
      let index = 0;
      index < 20;
      index++
    ) {
      this.availableYears.push(
        String(currentYear - index),
      );
    }
  }

  setDefaultYears(): void {
    const currentYear =
      new Date().getFullYear();

    this.years = [
      String(currentYear),
      String(currentYear - 1),
      String(currentYear - 2),
    ];
  }

  addYear(): void {
    if (!this.selectedYear) {
      return;
    }

    if (
      !this.years.includes(
        this.selectedYear,
      )
    ) {
      this.years.push(
        this.selectedYear,
      );

      this.sortYears();
    }

    // Re-adding a previously removed year should save it normally again,
    // not carry forward the pending delFlg 1 from its earlier removal.
    this.removedYears =
      this.removedYears.filter(
        (year) => year !== this.selectedYear,
      );

    this.selectedYear = '';
  }

  removeYear(year: string): void {
    this.years =
      this.years.filter(
        (item) => item !== year,
      );

    if (!this.removedYears.includes(year)) {
      this.removedYears = [
        ...this.removedYears,
        year,
      ];
    }
  }

  sortYears(): void {
    this.years = [
      ...this.years,
    ].sort(
      (first, second) =>
        Number(second) -
        Number(first),
    );
  }

  loadIncomeStatementMasterData(): void {
    this.setupService
      .getActiveIncomeStatementTypes()
      .subscribe({
        next: (typesResponse: any) => {
          const activeTypes =
            this.extractArray(
              typesResponse,
            ).filter(
              (item: any) =>
                this.isActiveRecord(item),
            );

          const statementTypeSequences =
            activeTypes
              .map((item: any) =>
                this.getStatementTypeSequence(
                  item,
                ),
              )
              .filter(
                (sequence: number) =>
                  sequence > 0,
              );

          if (
            statementTypeSequences.length === 0
          ) {
            this.financialSections = [];
            this.financialMasterLoaded = true;

            return;
          }

          this.setupService
            .getActiveIncomeStatementValues(
              statementTypeSequences,
            )
            .subscribe({
              next: (
                valuesResponse: any,
              ) => {
                const responseData =
                  valuesResponse?.data ??
                  valuesResponse ??
                  {};

                this.financialSections =
                  activeTypes
                    .map((type: any) => {
                      const statementTypeSequence =
                        this.getStatementTypeSequence(
                          type,
                        );

                      let sectionValues:
                        any[] = [];

                      if (
                        Array.isArray(
                          responseData,
                        )
                      ) {
                        sectionValues =
                          responseData.filter(
                            (value: any) =>
                              this.getValueStatementTypeSequence(
                                value,
                              ) ===
                              statementTypeSequence,
                          );
                      } else {
                        const groupedValues =
                          responseData[
                            statementTypeSequence
                          ] ??
                          responseData[
                            String(
                              statementTypeSequence,
                            )
                          ] ??
                          [];

                        sectionValues =
                          Array.isArray(
                            groupedValues,
                          )
                            ? groupedValues
                            : [];
                      }

                      const activeValues =
                        sectionValues
                          .filter(
                            (value: any) =>
                              this.isActiveRecord(
                                value,
                              ),
                          )
                          .sort(
                            (
                              first: any,
                              second: any,
                            ) =>
                              Number(
                                first?.sortOrdr ??
                                first?.sortOrder ??
                                0,
                              ) -
                              Number(
                                second?.sortOrdr ??
                                second?.sortOrder ??
                                0,
                              ),
                          );

                      return {
                        title:
                          type?.typNm?.trim() ||
                          type?.stmntTypNm?.trim() ||
                          type?.incmStmntTypNm?.trim() ||
                          type?.dscr?.trim() ||
                          '',

                        description:
                          type?.dscr ??
                          type?.description ??
                          '',

                        statementTypeSequence,

                        expanded: true,
                        checked: false,

                        rows:
                          activeValues
                            .map(
                              (
                                value: any,
                              ) => {
                                const valueSequence =
                                  this.getStatementValueSequence(
                                    value,
                                  );

                                return {
                                  label:
                                    value?.dscr ||
                                    value?.valNm ||
                                    value?.stmntValNm ||
                                    value?.incmStmntValNm ||
                                    '',

                                  valueSequence,

                                  checked: false,

                                  key:
                                    `income_${valueSequence}`,

                                  recordSequenceByYear:
                                    {},
                                };
                              },
                            )
                            .filter(
                              (row: any) =>
                                Number(
                                  row.valueSequence,
                                ) > 0,
                            ),
                      };
                    })
                    .filter(
                      (section: any) =>
                        Number(
                          section.statementTypeSequence,
                        ) > 0,
                    );

                this.financialMasterLoaded =
                  true;

                if (
                  this.crdtPrpslSeq &&
                  Number(
                    this.crdtPrpslSeq,
                  ) > 0
                ) {
                  this.loadSavedFinancialData();
                }
              },

              error: (error) => {
                this.financialSections = [];
                this.financialMasterLoaded =
                  false;

                console.error(
                  'Active income statement values load error:',
                  error,
                );
              },
            });
        },

        error: (error) => {
          this.financialSections = [];
          this.financialMasterLoaded =
            false;

          console.error(
            'Active income statement types load error:',
            error,
          );
        },
      });
  }

  loadSavedFinancialData(): void {
    const proposalSequence =
      Number(
        this.crdtPrpslSeq ??
        this.creditService.getCrdtPrpslSeq() ??
        0,
      );

    if (
      proposalSequence <= 0 ||
      !this.financialMasterLoaded
    ) {
      return;
    }

    this.loadFinancialProfile();

    this.loadingSavedData = true;

    this.creditService
      .getFinancialData(
        proposalSequence,
        this.INCOME_STATEMENT_TYPE,
      )
      .subscribe({
        next: (response: any) => {
          this.loadingSavedData = false;

          const savedData =
            this.extractFinancialData(
              response,
            );

          if (!savedData) {
            return;
          }

          this.mapSavedFinancialData(
            savedData,
          );

          this.applyFinancialProfile();
        },

        error: (error) => {
          this.loadingSavedData = false;

          console.error(
            'Get income statement data error:',
            error,
          );
        },
      });
  }

  private extractFinancialData(
    response: any,
  ): any {
    if (Array.isArray(response)) {
      return response[0] ?? null;
    }

    if (
      Array.isArray(response?.data)
    ) {
      return response.data[0] ?? null;
    }

    return (
      response?.data ??
      response ??
      null
    );
  }

  private mapSavedFinancialData(
    savedData: any,
  ): void {
    const allSavedRecords =
      Array.isArray(
        savedData?.crdtFnclDtaLst,
      )
        ? savedData.crdtFnclDtaLst
        : [];

    /**
     * typ = 2 means Income Statement.
     */
    const savedList =
      allSavedRecords.filter(
        (item: any) =>
          Number(
            item?.delFlg ?? 0,
          ) !== 1 &&
          Number(item?.typ) ===
            this.INCOME_STATEMENT_TYPE,
      );

    const firstSavedRecord =
      savedList[0];

    this.institutionName =
      String(
        savedData?.instNm ??
        this.institutionName ??
        '',
      ).trim();

    this.demographicModel = {
      ...this.removeExistingIncomeValues(
        this.demographicModel,
      ),

      institutionName:
        this.institutionName,

      /**
       * fnclType:
       * 0 = Audited
       * 1 = Management / Unaudited
       */
      financialStatementType:
        firstSavedRecord?.fnclType !==
          null &&
        firstSavedRecord?.fnclType !==
          undefined
          ? Number(
              firstSavedRecord.fnclType,
            )
          : this.demographicModel
              .financialStatementType ??
            '',

      reportingCurrency:
        savedData?.rprtngCrncy !==
          null &&
        savedData?.rprtngCrncy !==
          undefined
          ? Number(
              savedData.rprtngCrncy,
            )
          : '',

      financialYearEnd:
        this.toInputDate(
          savedData?.fnclYrEnd,
        ),

      isConsolidated:
        Number(
          savedData?.cnsldtdFncl,
        ) === 1,
    };

    const savedYears = [
      ...new Set<string>(
        savedList
          .map((item: any) =>
            item?.fncYr !== null &&
            item?.fncYr !== undefined
              ? String(item.fncYr)
              : '',
          )
          .filter(
            (year: string) =>
              year.length > 0,
          ),
      ),
    ];

    this.ownSavedYears = [...savedYears];

    if (
      savedYears.length > 0
    ) {
      this.years =
        savedYears.sort(
          (
            first: string,
            second: string,
          ) =>
            Number(second) -
            Number(first),
        );
    }

    this.financialSections.forEach(
      (section) => {
        section.checked = false;

        section.rows.forEach(
          (row: any) => {
            row.checked = false;
            row.recordSequenceByYear = {};
          },
        );
      },
    );

    savedList.forEach(
      (savedItem: any) => {
        const savedValueSequence =
          Number(
            savedItem?.valSeq ??
            savedItem?.refValSeq ??
            0,
          );

        if (
          savedValueSequence <= 0
        ) {
          return;
        }

        let matchedSection:
          any = null;

        let matchedRow:
          any = null;

        for (
          const section
          of this.financialSections
        ) {
          const row =
            section.rows.find(
              (item: any) =>
                Number(
                  item.valueSequence,
                ) ===
                savedValueSequence,
            );

          if (row) {
            matchedSection =
              section;

            matchedRow =
              row;

            break;
          }
        }

        if (
          !matchedSection ||
          !matchedRow
        ) {
          return;
        }

        const year =
          String(
            savedItem.fncYr,
          );

        matchedSection.checked =
          true;

        matchedRow.checked =
          true;

        this.demographicModel[
          this.getFieldKey(
            matchedRow.key,
            year,
          )
        ] = savedItem.val;

        matchedRow.recordSequenceByYear = {
          ...(
            matchedRow
              .recordSequenceByYear ||
            {}
          ),

          [year]:
            savedItem.crdtFnclDtaSeq,
        };
      },
    );
  }

  toggle(section: any): void {
    section.expanded =
      !section.expanded;
  }

  toggleSectionRows(
    section: any,
  ): void {
    section.rows.forEach(
      (row: any) => {
        row.checked =
          section.checked;
      },
    );
  }

  onRowCheckedChange(
    section: any,
  ): void {
    section.checked =
      section.rows.some(
        (row: any) =>
          row.checked === true,
      );
  }

  hasSelectedRows(
    section: any,
  ): boolean {
    return section.rows.some(
      (row: any) =>
        row.checked === true,
    );
  }

  getFieldKey(
    rowKey: string,
    year: string,
  ): string {
    return `${rowKey}_${year}`;
  }

  // ================= COMPUTED TOTALS =================

  /**
   * Total row label per section:
   * Income -> "Total Income"
   * Expenses -> "Total Expenses"
   * Other Incomes Expenses -> "Total Surplus/Deficit for the year"
   */
  getSectionTotalLabel(
    section: any,
  ): string {
    const title =
      this.normalizeLabel(
        section?.title,
      );

    if (title === 'income') {
      return 'INCOME_STATEMENT.TOTAL_INCOME';
    }

    if (title === 'expenses') {
      return 'INCOME_STATEMENT.TOTAL_EXPENSES';
    }

    return 'INCOME_STATEMENT.TOTAL_SURPLUS_DEFICIT';
  }

  getSectionTotalValue(
    section: any,
    year: string,
  ): number {
    const title =
      this.normalizeLabel(
        section?.title,
      );

    if (
      title === 'income' ||
      title === 'expenses'
    ) {
      return this.getSectionTotal(
        section,
        year,
      );
    }

    return this.getTotalSurplusDeficit(
      year,
    );
  }

  /**
   * Sum a section's selected rows for a year (computed in
   * code). Aggregate rows (Total.../Operating Surplus/etc.)
   * are excluded so they never double-count.
   */
  private getSectionTotal(
    section: any,
    year: string,
  ): number {
    return (section?.rows || []).reduce(
      (sum: number, row: any) => {
        if (!row.checked) {
          return sum;
        }

        if (
          this.isAggregateLabel(
            row.label,
          )
        ) {
          return sum;
        }

        const value = Number(
          this.demographicModel[
            this.getFieldKey(
              row.key,
              year,
            )
          ],
        );

        return (
          sum +
          (Number.isFinite(value)
            ? value
            : 0)
        );
      },
      0,
    );
  }

  private getSectionTotalByTitle(
    title: string,
    year: string,
  ): number {
    const target =
      this.normalizeLabel(title);

    const section =
      this.financialSections.find(
        (item: any) =>
          this.normalizeLabel(
            item.title,
          ) === target,
      );

    return section
      ? this.getSectionTotal(
          section,
          year,
        )
      : 0;
  }

  /**
   * Exact replica of the Excel formula:
   *   Total Surplus/Deficit for the year
   *   = (Total Income - Total Expenses)
   *     + Grant Income - Grant Expenses - Tax Expense
   *     + (Grant Income + Other Comprehensive Income/Expenses)
   *
   * NOTE: Grant Income is added twice, matching the sheet
   * formula (=C53+C54-C55-C56+C58, where C58 = C54+C57).
   */
  getTotalSurplusDeficit(
    year: string,
  ): number {
    const totalIncome =
      this.getSectionTotalByTitle(
        'income',
        year,
      );

    const totalExpenses =
      this.getSectionTotalByTitle(
        'expenses',
        year,
      );

    const operatingSurplus =
      totalIncome - totalExpenses;

    const grantIncome =
      this.getRowValueByYear(
        (n) => n === 'grant income',
        year,
      );

    const grantExpenses =
      this.getRowValueByYear(
        (n) =>
          n === 'grant expenses' ||
          n === 'grant expense',
        year,
      );

    const taxExpense =
      this.getRowValueByYear(
        (n) => n === 'tax expense',
        year,
      );

    const otherComprehensive =
      this.getRowValueByYear(
        (n) =>
          n.includes(
            'comprehensive',
          ) ||
          n.includes('comrehensive'),
        year,
      );

    const netGrantAndComp =
      grantIncome + otherComprehensive;

    return (
      operatingSurplus +
      grantIncome -
      grantExpenses -
      taxExpense +
      netGrantAndComp
    );
  }

  private getRowValueByYear(
    predicate: (
      normalizedLabel: string,
    ) => boolean,
    year: string,
  ): number {
    for (const section of this
      .financialSections) {
      for (const row of section.rows) {
        if (
          row.checked &&
          predicate(
            this.normalizeLabel(
              row.label,
            ),
          )
        ) {
          const value = Number(
            this.demographicModel[
              this.getFieldKey(
                row.key,
                year,
              )
            ],
          );

          if (
            Number.isFinite(value)
          ) {
            return value;
          }
        }
      }
    }

    return 0;
  }

  private isAggregateLabel(
    label: string,
  ): boolean {
    const n =
      this.normalizeLabel(label);

    return (
      n.startsWith('total ') ||
      n.includes(
        'operating surplus',
      ) ||
      n.includes('net grant') ||
      n.includes('surplus/deficit') ||
      n.includes('surplus deficit')
    );
  }

  onExcelParsed(parsed: ExcelParseResult | null): void {
    const result = this.excelImport.applyPatch(
      parsed,
      this.financialSections,
      this.demographicModel,
      this.years,
    );

    this.fileUploadError = result.message;
    this.demographicModel = { ...this.demographicModel };
    this.cdr.detectChanges();
  }

  private normalizeLabel(
    value: any,
  ): string {
    return String(value ?? '')
      .replace(/[-–—]/g, ' ')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  onFormChange(
    value: any,
  ): void {
    this.demographicModel = {
      ...this.demographicModel,
      ...value,
    };
  }

  onSubmit(value: any): void {
    this.onFormChange(value);
    this.saveFinancialData();
  }

  /** Loads the Financial section's settings, then applies them here. */
  private loadFinancialProfile(): void {
    this.financialProfileSub?.unsubscribe();

    this.financialProfileSub =
      this.financialProfileService
        .load(
          this.crdtPrpslSeq ??
          this.creditService.getCrdtPrpslSeq(),
        )
        .subscribe((profile) => {
          this.financialProfile = profile;
          this.financialProfileLoaded = true;
          this.applyFinancialProfile();
          this.cdr.markForCheck();
        });
  }

  /**
   * Single source of truth: once the Financial section is saved, its years,
   * Audited / Unaudited per year, reporting currency, year end and
   * consolidation are used here and shown read-only instead of being asked
   * again. Years this section saved that the Financial section no longer
   * has are removed (sent with delFlg 1) on the next save.
   */
  private applyFinancialProfile(): void {
    const profile = this.financialProfile;

    if (!profile) {
      this.demographicFields = this.ownFields;
      return;
    }

    this.demographicModel = {
      ...this.demographicModel,
      reportingCurrency: profile.reportingCurrency ?? '',
      financialYearEnd: profile.financialYearEnd,
      isConsolidated: profile.isConsolidated,
    };

    this.years = [...profile.years];

    this.removedYears = this.ownSavedYears.filter(
      (year) => !profile.years.includes(year),
    );

    this.demographicFields = this.ownFields
      .filter((field) => field.key !== 'financialStatementType')
      .map((field) => ({ ...field, disabled: true }));
  }

  /** Audited (0) / Unaudited (1) for a year as set in the Financial section. */
  statementTypeForYear(year: string): number | null {
    return this.financialProfile?.typeByYear[year] ?? null;
  }

  /**
   * fnclType saved for a year: the Financial section's choice for that year,
   * else this section's own selection (when the Financial section is not
   * saved yet), else Audited for rows that are only being deleted.
   */
  private resolveStatementType(
    year: string,
    sectionType: number | null,
  ): number {
    return (
      this.statementTypeForYear(year) ??
      sectionType ??
      0
    );
  }

  saveFinancialData(): void {
    if (!this.editable) {
      return;
    }

    this.errorMessage = '';
    this.successMessage = '';

    const proposalSequence =
      Number(
        this.crdtPrpslSeq ??
        this.creditService.getCrdtPrpslSeq() ??
        0,
      );

    if (
      !Number.isFinite(
        proposalSequence,
      ) ||
      proposalSequence <= 0
    ) {
      this.reportValidationError(
        'INCOME_STATEMENT.PROPOSAL_SEQUENCE_REQUIRED',
      );

      return;
    }

    const institutionName =
      String(
        this.demographicModel
          ?.institutionName ??
        this.institutionName ??
        '',
      ).trim();

    if (!institutionName) {
      this.reportValidationError(
        'INCOME_STATEMENT.INSTITUTION_NAME_REQUIRED',
      );

      return;
    }

    const reportingCurrencyValue =
      this.demographicModel
        .reportingCurrency;

    if (
      reportingCurrencyValue === null ||
      reportingCurrencyValue === undefined ||
      reportingCurrencyValue === ''
    ) {
      this.reportValidationError(
        'INCOME_STATEMENT.REPORTING_CURRENCY_REQUIRED',
      );

      return;
    }

    const reportingCurrency =
      Number(
        reportingCurrencyValue,
      );

    if (
      reportingCurrency !== 0 &&
      reportingCurrency !== 1
    ) {
      this.reportValidationError(
        'INCOME_STATEMENT.REPORTING_CURRENCY_INVALID',
      );

      return;
    }

    /**
     * fnclType:
     * 0 = Audited
     * 1 = Management / Unaudited
     */
    const financialStatementType =
      this.normalizeStatementType(
        this.demographicModel
          .financialStatementType,
      );

    if (this.financialProfile) {
      const yearWithoutType = this.years.find(
        (year) => this.statementTypeForYear(year) === null,
      );

      if (yearWithoutType) {
        this.reportValidationError(
          'FINANCIAL_PROFILE.TYPE_MISSING',
          { year: yearWithoutType },
        );

        return;
      }
    } else if (
      financialStatementType === null
    ) {
      this.reportValidationError(
        'INCOME_STATEMENT.FINANCIAL_STATEMENT_TYPE_REQUIRED',
      );

      return;
    }

    const financialYearEnd =
      this.formatApiDate(
        this.demographicModel
          .financialYearEnd,
      );

    if (!financialYearEnd) {
      this.reportValidationError(
        'INCOME_STATEMENT.FINANCIAL_YEAR_END_REQUIRED',
      );

      return;
    }

    const financialDataList =
      this.buildFinancialDataList(
        financialStatementType,
      );

    if (
      !financialDataList.length
    ) {
      this.reportValidationError(
        'INCOME_STATEMENT.SELECT_AT_LEAST_ONE',
      );

      return;
    }

    const payload = {
      crdtPrpslSeq:
        proposalSequence,

      rprtngCrncy:
        reportingCurrency,

      fnclYrEnd:
        financialYearEnd,

      cnsldtdFncl:
        this.toNumberFlag(
          this.demographicModel
            .isConsolidated,
        ),

      instNm:
        institutionName,

      crdtFnclDtaLst:
        financialDataList,
    };

    console.log(
      'Income Statement payload:',
      payload,
    );

    this.saving = true;

    this.creditService
      .saveFinancialData(payload)
      .subscribe({
        next: (response: any) => {
          this.saving = false;

          this.successMessage =
            this.translate.instant('INCOME_STATEMENT.SAVE_SUCCESS');

          const returnedSequence =
            Number(
              response?.crdtPrpslSeq ??
              response?.data?.crdtPrpslSeq ??
              proposalSequence,
            );

          if (
            returnedSequence > 0
          ) {
            this.creditService
              .setCrdtPrpslSeq(
                returnedSequence,
              );
          }

          this.toastr.success(
            this.successMessage,
          );

          this.removedYears = [];

          this.loadSavedFinancialData();

          this.creditService.reportSectionSaveResult({
            sectionId: this.sectionId,
            status: 'saved',
          });
        },

        error: (error) => {
          this.saving = false;

          this.errorMessage =
            error?.error?.message ??
            this.translate.instant('INCOME_STATEMENT.SAVE_FAILED');

          console.error(
            'Save income statement data error:',
            error,
          );

          this.toastr.error(
            this.errorMessage,
          );

          this.creditService.reportSectionSaveResult({
            sectionId: this.sectionId,
            status: 'api-error',
            message: this.errorMessage,
          });
        },
      });
  }

  private reportValidationError(
    messageKey: string,
    params?: Record<string, unknown>,
  ): void {
    const message = this.translate.instant(messageKey, params);

    this.errorMessage = message;

    console.error(message);

    this.toastr.warning(message);

    /*
     * Report the outcome back so the section-save
     * orchestrator (document-builder-page) can stop
     * waiting instead of timing out after 30s.
     */
    this.creditService.reportSectionSaveResult({
      sectionId: this.sectionId,
      status: 'validation-error',
      message,
    });
  }

  private buildFinancialDataList(
    financialStatementType: number | null,
  ): any[] {
    const records: any[] = [];

    // Removed years no longer render in `years`, but their previously saved
    // rows still need a delFlg 1 record — union the two so they aren't
    // silently dropped from the save payload.
    const yearsToProcess = [
      ...this.years,
      ...this.removedYears.filter(
        (year) => !this.years.includes(year),
      ),
    ];

    this.financialSections.forEach(
      (section) => {
        section.rows.forEach(
          (row: any) => {
            yearsToProcess.forEach(
              (year: string) => {
                const fieldKey =
                  this.getFieldKey(
                    row.key,
                    year,
                  );

                const enteredValue =
                  this.demographicModel[
                    fieldKey
                  ];

                const hasValue =
                  enteredValue !== null &&
                  enteredValue !== undefined &&
                  enteredValue !== '';

                const numericValue =
                  Number(
                    enteredValue,
                  );

                const isSelected =
                  row.checked === true;

                const previousSequence =
                  this.toNullableNumber(
                    row.recordSequenceByYear
                      ?.[year],
                  );

                const isRemovedYear =
                  this.removedYears.includes(
                    year,
                  );

                if (
                  !isRemovedYear &&
                  isSelected &&
                  hasValue &&
                  Number.isFinite(
                    numericValue,
                  )
                ) {
                  records.push({
                    crdtFnclDtaSeq:
                      previousSequence,

                    valSeq:
                      Number(
                        row.valueSequence,
                      ),

                    val:
                      numericValue,

                    delFlg:
                      0,

                    /**
                     * 0 = Audited
                     * 1 = Management / Unaudited
                     */
                    fnclType:
                      this.resolveStatementType(
                        year,
                        financialStatementType,
                      ),

                    fncYr:
                      Number(year),

                    /**
                     * Fixed Income Statement type.
                     */
                    typ:
                      this.INCOME_STATEMENT_TYPE,
                  });

                  return;
                }

                if (
                  previousSequence !== null
                ) {
                  records.push({
                    crdtFnclDtaSeq:
                      previousSequence,

                    valSeq:
                      Number(
                        row.valueSequence,
                      ),

                    val:
                      0,

                    delFlg:
                      1,

                    fnclType:
                      this.resolveStatementType(
                        year,
                        financialStatementType,
                      ),

                    fncYr:
                      Number(year),

                    typ:
                      this.INCOME_STATEMENT_TYPE,
                  });
                }
              },
            );
          },
        );
      },
    );

    return records;
  }

  getReportingCurrencyLabel(
    value: unknown,
  ): string {
    if (
      value === null ||
      value === undefined ||
      value === ''
    ) {
      return '-';
    }

    const currency =
      Number(value);

    if (currency === 0) {
      return 'PKR';
    }

    if (currency === 1) {
      return 'USD';
    }

    return '-';
  }

  getFinancialStatementTypeLabel(
    value: unknown,
  ): string {
    const type =
      this.normalizeStatementType(
        value,
      );

    if (type === 0) {
      return 'INCOME_STATEMENT.AUDITED';
    }

    if (type === 1) {
      return 'INCOME_STATEMENT.MANAGEMENT_UNAUDITED';
    }

    return '-';
  }

  private normalizeStatementType(
    value: unknown,
  ): number | null {
    if (
      value === null ||
      value === undefined ||
      value === ''
    ) {
      return null;
    }

    const type =
      Number(value);

    if (
      type === 0 ||
      type === 1
    ) {
      return type;
    }

    return null;
  }

  private toNullableNumber(
    value: unknown,
  ): number | null {
    if (
      value === null ||
      value === undefined ||
      value === ''
    ) {
      return null;
    }

    const numericValue =
      Number(value);

    return Number.isFinite(
      numericValue,
    )
      ? numericValue
      : null;
  }

  private extractArray(
    response: any,
  ): any[] {
    if (Array.isArray(response)) {
      return response;
    }

    if (
      Array.isArray(response?.data)
    ) {
      return response.data;
    }

    if (
      Array.isArray(response?.content)
    ) {
      return response.content;
    }

    if (
      Array.isArray(
        response?.data?.content,
      )
    ) {
      return response.data.content;
    }

    return [];
  }

  private isActiveRecord(
    item: any,
  ): boolean {
    const activeFlag =
      item?.actvFlg;

    const deleteFlag =
      item?.delFlg;

    const isActive =
      activeFlag === undefined ||
      activeFlag === null ||
      activeFlag === true ||
      activeFlag === 1 ||
      activeFlag === '1';

    const isDeleted =
      deleteFlag === true ||
      deleteFlag === 1 ||
      deleteFlag === '1';

    return (
      isActive &&
      !isDeleted
    );
  }

  private getStatementTypeSequence(
    item: any,
  ): number {
    return Number(
      item?.stmntTypSeq ??
      item?.incmStmntTypSeq ??
      item?.incomeStatementTypeSeq ??
      item?.typSeq ??
      item?.fncTypSeq ??
      0,
    );
  }

  private getValueStatementTypeSequence(
    item: any,
  ): number {
    return Number(
      item?.stmntTypSeq ??
      item?.incmStmntTypSeq ??
      item?.incomeStatementTypeSeq ??
      item?.stmntTyp ??
      item?.fncTyp ??
      item?.typSeq ??
      0,
    );
  }

  private getStatementValueSequence(
    item: any,
  ): number {
    return Number(
      item?.stmntValSeq ??
      item?.incmStmntValSeq ??
      item?.incomeStatementValueSeq ??
      item?.valSeq ??
      item?.fncValSeq ??
      0,
    );
  }

  private removeExistingIncomeValues(
    model: any,
  ): any {
    return Object.entries(
      model || {},
    ).reduce(
      (
        result: any,
        [key, value],
      ) => {
        if (
          !key.startsWith(
            'income_',
          )
        ) {
          result[key] = value;
        }

        return result;
      },
      {},
    );
  }

  private toNumberFlag(
    value: any,
  ): number {
    return (
      value === true ||
      value === 'true' ||
      value === 1 ||
      value === '1'
    )
      ? 1
      : 0;
  }

  private toInputDate(
    value: any,
  ): string {
    if (!value) {
      return '';
    }

    return String(value)
      .slice(0, 10);
  }

  private formatApiDate(
    value: any,
  ): string {
    if (!value) {
      return '';
    }

    if (
      typeof value === 'string'
    ) {
      return value.slice(
        0,
        10,
      );
    }

    const date =
      new Date(value);

    if (
      Number.isNaN(
        date.getTime(),
      )
    ) {
      return '';
    }

    return date
      .toISOString()
      .slice(0, 10);
  }
}